import { useState, useEffect } from 'react';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { getOrganizationLoanOfficers } from '@/services/loanPricingService';

const log = createComponentLogger('useAvailableLoanOfficers', 'encompass-prospects');

export interface LoanOfficerOption {
  /**
   * Value used for `filters.loanOfficerId`. Must match `encompass_loans.loan_officer_id`
   * (the Encompass user id, not the Lendwise auth user id). Falls back to the auth id
   * only when the user has no linked Encompass id.
   */
  value: string;
  label: string;
  count: number;
}

/**
 * Fetches the organization's loan-officer roster for the redesigned active Loan Center
 * Officer filter. Option value is `encompassUserId ?? id` so it aligns with the existing
 * `filters.loanOfficerId` semantics consumed by `matchesProspectFilter` and
 * `search_encompass_loans.p_loan_officer_id`.
 *
 * Returns real loan counts per LO, plus an "Unassigned" bucket, so consumers can render
 * "LO Name (12)" style chips.
 */
export function useAvailableLoanOfficers(organizationId: string | undefined) {
  const [officers, setOfficers] = useState<LoanOfficerOption[]>([]);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) {
      setOfficers([]);
      setUnassignedCount(0);
      setIsLoading(false);
      return;
    }

    const fetchOfficers = async () => {
      setIsLoading(true);
      try {
        const roster = await getOrganizationLoanOfficers();

        const { data: loanData, error: loanError } = await supabase
          .from('encompass_loans')
          .select('loan_officer_id')
          .eq('organization_id', organizationId)
          .eq('deleted_from_encompass', false)
          .neq('loan_folder', 'Transferred Data');

        if (loanError) {
          log.error('Failed to fetch loan_officer_id counts', { organizationId }, loanError);
        }

        const counts = new Map<string, number>();
        let unassigned = 0;
        for (const row of loanData || []) {
          const id = row.loan_officer_id;
          if (id) {
            counts.set(id, (counts.get(id) || 0) + 1);
          } else {
            unassigned++;
          }
        }

        const result: LoanOfficerOption[] = roster.map(lo => ({
          value: lo.encompassUserId ?? lo.id,
          label: lo.fullName,
          count: counts.get(lo.encompassUserId ?? lo.id) || 0
        }));

        result.sort((a, b) => a.label.localeCompare(b.label));

        log.info('Loaded loan officers', {
          organizationId,
          rosterCount: roster.length,
          withLoans: result.filter(o => o.count > 0).length,
          unassigned
        });

        setOfficers(result);
        setUnassignedCount(unassigned);
      } catch (err) {
        log.error('Error fetching loan officers', { organizationId }, err as Error);
        setOfficers([]);
        setUnassignedCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOfficers();
  }, [organizationId]);

  return { officers, unassignedCount, isLoading };
}
